import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, filter, mergeMap, switchMap, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import {
  ConfirmationReasons,
  EntityId,
  IComment,
  INewComment,
  IReconcileReason,
  IReconcileTicketDetail,
  IReconcileTicketReasons,
  ISidebarDetail,
  ISidebarDetailData,
  PortalAuthority,
  ReconcileStatus,
  TicketStatus,
} from '@appshared';
import { AclService } from '@aes/acl/acl.service';
import { AccountAbility } from '@aes/acl';

import { locale as vn } from './i18n/vn';
import { locale as english } from './i18n/en';
import { ReconciledDetailApiService, ReconciledDetailTicketService } from '../services';
import { SimpleCommentComponent } from './simple-comment';
import { NotificationService } from '../../../../@aes/core';


@Component({
  selector: 'reconciled-detail-sidebar',
  templateUrl: './reconciled-detail-sidebar.component.html',
  styleUrls: ['./reconciled-detail-sidebar.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ReconciledDetailComponent implements ISidebarDetail, OnDestroy, OnInit {
  readonly RECONCILE_STATUS_ENUM = ReconcileStatus;
  readonly TICKET_STATUS_ENUM = TicketStatus;
  readonly PORTAL_AUTHORITY_ENUM = PortalAuthority;
  readonly CONFIRMATION_REASONS_ENUM = ConfirmationReasons;

  readonly emptyValue = '--';

  _unsubscribeAll = new Subject();
  reconciledDetail: IReconcileTicketDetail;
  confirmationForm: FormGroup;
  shippingAddress: string = null;
  reconcileTicketReasons: IReconcileTicketReasons;
  confirmationReasonList: IReconcileReason[] = [];
  ability: string[] = [];
  showDescriptionTxt = false;
  lang: string;

  @ViewChild(SimpleCommentComponent) commentComponent: SimpleCommentComponent;

  constructor(
    private _formBuilder: FormBuilder,
    private _translateService: TranslateService,
    private _fuseTranslationLoaderService: FuseTranslationLoaderService,
    private _aclService: AclService,
    public _reconcileTicketDetailSrv: ReconciledDetailTicketService,
    public _reconciledApiSrv: ReconciledDetailApiService,
    private notificationService: NotificationService
  ) {
    this._fuseTranslationLoaderService.loadTranslations(english, vn);
  }

  onInputChange(data: ISidebarDetailData): void {
    // reset all, including validator rules, and also clear all data from previous ticket
    this._resetComponentData();
    this.getDetail(data.reconcileItemId);

    // when viewing the detail, user won't be able to change the language. If they want, they have to close the current detail view first.
    // Therefore, we only need to get the language code once when new detail is loaded.
    this.lang = this._translateService.currentLang;

    // correct the difference between BE & FE's language code
    this.lang = this.lang === 'vn' ? 'vi' : this.lang;
  }

  ngOnInit(): void {
    this.setupForm();
    this._aclService
      .getAccountAbilityState()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((ability: AccountAbility) => (this.ability = [...ability.permissions, ...ability.functions]));

    this._reconcileTicketDetailSrv
      .getListReason()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((data: IReconcileTicketReasons) => (this.reconcileTicketReasons = data));
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  private setupForm(): void {
    this.confirmationForm = this._formBuilder.group({
      selectReason: [null],
      descriptionTxt: [null],
    });
    this.handleReconcileReasonsChange();
  }

  private _resetComponentData(): void {
    // Quickly reset all the form data by reassign it again, to avoid a potential error from Material library
    // More info at: https://github.com/angular/material2/issues/4190
    this.setupForm();

    // then call on children component, let them do their stuffs
    this.commentComponent?.resetData();

    // to ensure no old data is leaving behind
    this.reconciledDetail = null;
  }

  // From ARTP-1046: "I can close the detail sidebar by clicking the Close icon on the header or clicking outside."
  // currently, click on the overlay will immediately close the view
  closeView(): void {
    // close the view
    this._reconcileTicketDetailSrv.closeView();
  }

  // Will display the Archive Dialog if needed. If not, return `true`
  // If display, will return `true` if user still want to confirm,
  // otherwise will return `false`
  private _displayArchiveDialog(): Observable<boolean> {
    if (
      this.reconciledDetail.reconcileStatus === ReconcileStatus.TX_NOT_FOUND &&
      this.confirmationForm.get('selectReason').value === ConfirmationReasons.DUPLICATE_ORDER
    ) {
      return this._reconcileTicketDetailSrv.showDuplicateOrderModal().pipe(
        switchMap(confirmed => {
          // If user selects 'No' on the "Archive the duplicate Dialog" popup, we reset selectReason value
          if (!confirmed) {
            this.confirmationForm.get('selectReason').patchValue(null);

            // and return 'false' indicate that user doesn't want to process
            return of(false);
          }

          // user want to process
          return of(true);
        }),
        takeUntil(this._unsubscribeAll)
      );
    } else return of(true);
  }

  onConfirmReason(): void {
    if (this.confirmationForm.invalid) {
      return;
    }

    // Check to display "Archive the duplicate Dialog" or not
    // If display, will show confirm modal and listen to result
    this._displayArchiveDialog()
      .pipe(
        mergeMap(toConfirm => {
          // If simply there is no need for displaying Archive Dialog
          // or if user acknowledge the warning, continue
          if (toConfirm) return this._reconcileTicketDetailSrv.confirmReason(this.reconciledDetail.ticketId, this.confirmationForm.value);

          return of(null);
        }),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe((res: any) => {
        // if there is res.success from confirm API, display the successfull dialog
        if (res?.success) {
          this._reconcileTicketDetailSrv.showSuccesfulDialog();
          this.closeView();
        }
      });
  }

  onApprove(): void {
    this._reconcileTicketDetailSrv
      .updateTicketStatus(this.reconciledDetail.ticketId, TicketStatus.COMPLETED)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(
        (res: any) => {
          if (res.success) {
            this._reconcileTicketDetailSrv.showStatusDialog(
              'DIALOG.APPROVAL_SUCCESS',
              'DIALOG.APPROVAL_SUCCESS_MSG',
              this.reconciledDetail.ticketId
            );
            this.closeView();
          }
        },
        (error: any) => {
          console.warn(error);
        }
      );
  }

  onRequestModification(): void {
    this._reconcileTicketDetailSrv
      .updateTicketStatus(this.reconciledDetail.ticketId, TicketStatus.REQUESTMODIFICATION)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(
        (res: any) => {
          if (res.success) {
            this._reconcileTicketDetailSrv.showStatusDialog(
              'DIALOG.REQUEST_SUCCESS',
              'DIALOG.REQUEST_SUCCESS_MSG',
              this.reconciledDetail.ticketId
            );
            this.closeView();
          }
        },
        (error: any) => {
          console.warn(error);
        }
      );
  }

  isMakerRole(): boolean {
    // for now, maker role is determined by PERM_CONFIRM_SALE_DATA
    return this.ability?.includes(this.PORTAL_AUTHORITY_ENUM.PERM_CONFIRM_SALE_DATA);
  }

  // will disable Confirm & Comment Section for Maker
  isMakerViewMode(): boolean {
    return (
      this.isMakerRole() &&
      (this.reconciledDetail.status === TicketStatus.WAITINGAPPROVAL || this.reconciledDetail.status === TicketStatus.COMPLETED)
    );
  }

  isCheckerMode(): boolean  {
      return (!this.isMakerRole() && this.reconciledDetail.status === TicketStatus.COMPLETED);
  }

  private getDetail(id: EntityId): void {
    this._reconcileTicketDetailSrv
      .getTicketDetail(id)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((data: IReconcileTicketDetail) => {
        this.reconciledDetail = { ...data };
        this.shippingAddress = this._reconcileTicketDetailSrv.getShippingAddress(data.orderInfo?.shippingAddress) || this.emptyValue;

        // get list reasons
        this.confirmationReasonList = this._reconcileTicketDetailSrv.getConfirmationReasons(
          this.reconcileTicketReasons,
          this.reconciledDetail.reconcileStatus
        );

        // set the values of reason & reason explaination
        this.confirmationForm.reset({
          selectReason: this.reconciledDetail.reason,
          descriptionTxt: this.reconciledDetail.reasonExplanation,
        });

        // Depend on the ticket's Reconcile Type and Status, will disable select box or descriptionTxt
        if (this.reconciledDetail.reconcileStatus === ReconcileStatus.RECONCILED) {
          this.confirmationForm.get('selectReason').disable();
        }
        if (
          this.isMakerViewMode() ||
          !this.ability.includes(this.PORTAL_AUTHORITY_ENUM.PERM_CONFIRM_SALE_DATA) ||
          this.reconciledDetail.reconcileStatus === ReconcileStatus.RECONCILED
        ) {
          this.confirmationForm.get('descriptionTxt').disable();
        }
      });
  }

  private handleReconcileReasonsChange(): void {
    // Watch change for selectReason value
    this.confirmationForm
      .get('selectReason')
      .valueChanges.pipe(
        distinctUntilChanged(),
        filter((selectedValue: string) => !!selectedValue),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe((selectedValue: string) => {
        // In those cases, will display the “Mã tham chiếu” textarea for user to input value
        switch (this.reconciledDetail.reconcileStatus) {
          case ReconcileStatus.TX_NOT_FOUND: {
            switch (selectedValue) {
              case ConfirmationReasons.GIFT_ORDER:
              case ConfirmationReasons.DUPLICATE_ORDER:
              case ConfirmationReasons.PAY_ANOTHER_METHOD:
                this.showDescriptionTxt = false;
                break;
              default:
                this.showDescriptionTxt = true;
            }
            break;
          }
          case ReconcileStatus.ORDER_NOT_FOUND: {
            switch (selectedValue) {
              case ConfirmationReasons.NO_ORDER:
              case ConfirmationReasons.REFUNDING_ORDER:
              case ConfirmationReasons.ADDITIONAL_PAYMENT:
                this.showDescriptionTxt = false;
                break;
              default:
                this.showDescriptionTxt = true;
            }
            break;
          }
          // display the “Diễn giải" textarea
          default:
            this.showDescriptionTxt = true;
        }
      });
  }

  addNewComment(newComment: INewComment): void {
    this._reconcileTicketDetailSrv
      .newComment(this.reconciledDetail.ticketId, newComment)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((addedComment: IComment) => {
        // add newest one to the top
        this.reconciledDetail.comments.unshift(addedComment);
      });
  }

  get totalPayment(): number {
    // The orderInfo?.total is : (item’s price * quantity) + shippingFee - discountFee
    // The totalPayment is the (item’s price * quantity): orderInfo?.total - shippingFee + discountFee
    if (!this.reconciledDetail.orderInfo) {
      return null;
    }
    return (
      this.reconciledDetail.orderInfo?.total + this.reconciledDetail.orderInfo?.discountFee - this.reconciledDetail.orderInfo?.shippingFee
    );
  }

  private stringOrNull(text: string): string {
    return text && text !== '__' ? text : null;
  }

  isPendingStatus(): boolean {
    return !(
      this.reconciledDetail.status === TicketStatus.PENDING ||
      this.reconciledDetail.status === TicketStatus.REQUESTMODIFICATION ||
      this.reconciledDetail.status === TicketStatus.COMPLETED
    );
  }

  copyToClipboard(el): void {
    var r = document.createRange();
    r.selectNode(el);
    window.getSelection().removeAllRanges();
    window.getSelection().addRange(r);
    document.execCommand('copy');
    window.getSelection().removeAllRanges();
    this.notificationService.success(this._translateService.instant('COPPIED') + ' ' + r.toString());
  }
}
